import { useCallback, useEffect, useState } from 'react'
import { Button, DatePicker, Form, Input, Modal, Radio, Select, Table, Tooltip, Typography, notification } from 'antd'
import styled from 'styled-components'
import moment, { Moment } from 'moment'

import {
	faMagnifyingGlass,
	faNewspaper,
	faPlus,
	faQuestionCircle,
	faRadio,
	faTvRetro,
} from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { txt } from '../../../locales/i18n'
import { SharedTable } from '../../shared/table'
import { DeleteModal } from '../../shared/delete-modal'
import * as PressService from '../../../services/bb-api/press'

type PressItem = {
	id: number
	date: Moment
	type: 'article' | 'video' | 'radio'
	title: string
	media: string
	url: string
}

export const PressList = () => {
	const [pressItems, setPressItems] = useState<PressItem[]>([])
	const [loading, setLoading] = useState(true)
	const [pressItemSelected, setPressItemSelected] = useState<PressItem | null>(null)
	const [createModalVissible, setCreateModalVissible] = useState(false)
	const [form] = Form.useForm()

	const getPressItems = useCallback(async (values: { search: string; type: 'all' | 'article' | 'video' | 'radio' }) => {
		setLoading(true)

		try {
			const pressItems = (await PressService.query(values)) as PressItem[]
			pressItems.forEach((pressItem) => (pressItem.date = moment(pressItem.date)))
			setPressItems(pressItems)
		} catch (error) {
			const message = txt('notification.unknown error')
			notification.error({ message })
		}

		setLoading(false)
	}, [])

	useEffect(() => {
		const values = form.getFieldsValue()
		getPressItems(values)
	}, [getPressItems, form])

	const renderDetails = (pressItem: PressItem) => {
		const typeIcons: { [k: string]: IconDefinition } = {
			article: faNewspaper,
			video: faTvRetro,
			radio: faRadio,
		}

		return (
			<div className="flex horizontal gap">
				<FontAwesomeIcon icon={typeIcons[pressItem.type] ?? faQuestionCircle} size="2x" color="dodgerblue" />

				<div className="flex vertical">
					<Typography.Text type="secondary">{moment(pressItem.date).format('ll')}</Typography.Text>
					<Typography.Text ellipsis>{pressItem.title}</Typography.Text>
				</div>
			</div>
		)
	}

	const renderMedia = (pressItem: PressItem) => (
		<div className="flex vertical">
			<Typography.Text ellipsis>{pressItem.media}</Typography.Text>
			<Typography.Link onClick={(event) => event.stopPropagation()} ellipsis href={pressItem.url} target="_blank">
				{pressItem.url}
			</Typography.Link>
		</div>
	)

	const create = async (pressItem: PressItem) => {
		try {
			await PressService.create(pressItem)
		} catch (error) {
			const message = txt('notification.create failed')
			return notification.error({ message })
		}

		const message = txt('notification.create successful')
		notification.success({ message })

		const values = form.getFieldsValue()
		getPressItems(values)
		setCreateModalVissible(false)
	}

	const update = async (pressItem: PressItem) => {
		const pressItemId = pressItemSelected!.id

		try {
			await PressService.update(pressItemId, pressItem)
		} catch (error) {
			const message = txt('notification.update failed')
			return notification.error({ message })
		}

		const message = txt('notification.update successful')
		notification.success({ message })

		setPressItemSelected(null)

		const values = form.getFieldsValue()
		getPressItems(values)
	}

	const remove = async () => {
		const pressItemId = pressItemSelected!.id
		await PressService.remove(pressItemId)

		setPressItemSelected(null)

		const values = form.getFieldsValue()
		getPressItems(values)
	}

	return (
		<PressListStyled>
			<section>
				<Form form={form} layout="horizontal" initialValues={{ type: 'all' }} onFinish={getPressItems}>
					<Tooltip title={txt('global.create')}>
						<Button
							type="link"
							size="small"
							icon={<FontAwesomeIcon icon={faPlus} />}
							onClick={() => setCreateModalVissible(true)}
						/>
					</Tooltip>

					<Tooltip title={txt('global.search')}>
						<span>
							<Form.Item name="search" noStyle>
								<Input placeholder="Search" size="small" allowClear />
							</Form.Item>
						</span>
					</Tooltip>

					<span>
						<Form.Item name="type" noStyle>
							<Select dropdownMatchSelectWidth={false} size="small">
								<Select.Option value="all">{txt('global.all')}</Select.Option>
								<Select.Option value="article">{txt('global.article')}</Select.Option>
								<Select.Option value="video">{txt('global.tv')}</Select.Option>
								<Select.Option value="radio">{txt('global.radio')}</Select.Option>
							</Select>
						</Form.Item>
					</span>

					<Tooltip title={txt('global.search')}>
						<Button type="link" htmlType="submit" size="small" icon={<FontAwesomeIcon icon={faMagnifyingGlass} />} />
					</Tooltip>
				</Form>
			</section>

			<section className="table-wrapper">
				<SharedTable
					rowKey="id"
					loading={loading}
					dataSource={pressItems}
					onRow={(postItem) => ({ onClick: () => setPressItemSelected(postItem) })}
				>
					<Table.Column title={txt('global.date')} render={renderDetails} />
					<Table.Column title={txt('global.media')} render={renderMedia} />
				</SharedTable>
			</section>

			<PressItemModal
				visible={createModalVissible}
				submitButtonText={txt('global.create')}
				onCancel={() => setCreateModalVissible(false)}
				onSubmit={create}
			/>

			<PressItemModal
				pressItem={pressItemSelected}
				visible={pressItemSelected !== null}
				submitButtonText={txt('global.update')}
				onCancel={() => setPressItemSelected(null)}
				onSubmit={update}
				onDelete={remove}
			/>
		</PressListStyled>
	)
}

const PressListStyled = styled.main`
	display: flex;
	flex-direction: column;
	gap: 1rem;

	background-color: ghostwhite;
	padding: 1rem;

	section {
		border-radius: 1rem;
		background-color: white;
		box-shadow: 0 0 1rem #89a3;
	}

	form {
		margin: 1rem;

		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: center;
		gap: 1rem;
	}

	.table-wrapper {
		overflow: auto;
	}

	.flex {
		display: flex;
		overflow: hidden;
	}

	.vertical {
		flex-direction: column;
	}

	.horizontal {
		flex-direction: row;
		align-items: center;
	}

	.gap {
		gap: 1rem;
	}
`

type PressItemModalProps = {
	pressItem?: PressItem | null
	onSubmit: (values: PressItem) => void | Promise<void>
	onCancel: () => void
	submitButtonText: string
	visible: boolean
	onDelete?: () => void | Promise<void>
}

const requiredRule = { required: true, message: txt('global.required') }

const PressItemModal = (props: PressItemModalProps) => {
	const [loading, setLoading] = useState(false)
	const [deleteModalVissible, setDeleteModalVissible] = useState(false)
	const [form] = Form.useForm()

	useEffect(() => {
		if (form.setFieldsValue) form.setFieldsValue(props.pressItem)
	}, [form, props.pressItem])

	const submit = async () => {
		setLoading(true)
		form.submit()
		setLoading(false)
	}

	const renderFooter = () => (
		<div className="flex push-right">
			{props.pressItem && (
				<Button danger onClick={() => setDeleteModalVissible(true)} className="margin-right-auto">
					{txt('global.delete')}
				</Button>
			)}

			<Button onClick={props.onCancel}>{txt('global.cancel')}</Button>

			<Button type="primary" onClick={submit}>
				{props.submitButtonText}
			</Button>
		</div>
	)

	return (
		<PressItemModalStyled
			title={props.submitButtonText}
			destroyOnClose
			maskClosable
			visible={props.visible}
			onCancel={props.onCancel}
			okButtonProps={{ loading }}
			okText={props.submitButtonText}
			onOk={submit}
			footer={renderFooter()}
			forceRender
			afterClose={() => form.resetFields()}
		>
			<Form initialValues={props.pressItem ?? {}} form={form} onFinish={props.onSubmit}>
				<div className="flex horizontal centered gap">
					<div>
						<label>{txt('global.date')}</label>
						<Form.Item name="date" rules={[requiredRule]}>
							<DatePicker />
						</Form.Item>
					</div>

					<div>
						<label>{txt('global.type')}</label>
						<Form.Item name="type" rules={[requiredRule]}>
							<Radio.Group>
								<Radio value="article">{txt('global.article')}</Radio>
								<Radio value="video">{txt('global.tv')}</Radio>
								<Radio value="radio">{txt('global.radio')}</Radio>
							</Radio.Group>
						</Form.Item>
					</div>
				</div>

				<div>
					<label>{txt('global.title')}</label>
					<Form.Item name="title" rules={[requiredRule]}>
						<Input.TextArea />
					</Form.Item>
				</div>

				<div>
					<label>{txt('global.media')}</label>
					<Form.Item name="media" rules={[requiredRule]}>
						<Input />
					</Form.Item>
				</div>

				<div>
					<label>{txt('global.url')}</label>
					<Form.Item name="url" rules={[requiredRule]}>
						<Input.TextArea />
					</Form.Item>
				</div>
			</Form>

			<DeleteModal
				useVisible={[deleteModalVissible, setDeleteModalVissible]}
				onConfirm={async () => await props.onDelete?.()}
			/>
		</PressItemModalStyled>
	)
}

const PressItemModalStyled = styled(Modal)`
	.flex {
		display: flex;
	}

	.horizontal {
		flex-direction: row;
	}

	.gap {
		gap: 1rem;
	}

	.centered {
		align-items: center;
		justify-content: center;
	}

	.margin-right-auto {
		margin-right: auto;
	}

	.push-right {
		justify-content: flex-end;
	}
`
